import React from 'react';
import { Sparkles } from 'lucide-react';

/**
 * Digital Travel Identity — Initiative tagging.
 * Operator picks one or more of three brand initiatives on a vendor
 * (Activity / Hotel / Guide / Driver). On the public share maps, tagged
 * vendors get a subtle coloured halo around their pin and a "✨ A Pollen
 * Dot of Care" line in the popup.
 */
export const INITIATIVES = [
  {
    key: 'smiles',
    label: 'Smiles',
    full: 'Ambassadors of Smiles',
    color: '#f59e0b', // amber-500
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-800',
    activeBg: 'bg-amber-500',
    activeBorder: 'border-amber-600',
    activeText: 'text-white',
  },
  {
    key: 'cultural-exchange',
    label: 'Cultural Exchange',
    full: 'Architects of Cultural Exchange',
    color: '#c2410c', // terracotta (orange-700)
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    text: 'text-orange-800',
    activeBg: 'bg-orange-700',
    activeBorder: 'border-orange-800',
    activeText: 'text-white',
  },
  {
    key: 'sustainability',
    label: 'Sustainability',
    full: 'Stewards of Sustainability',
    color: '#84cc16', // sage (lime-500)
    bg: 'bg-lime-50',
    border: 'border-lime-200',
    text: 'text-lime-800',
    activeBg: 'bg-lime-600',
    activeBorder: 'border-lime-700',
    activeText: 'text-white',
  },
];

export const initiativeColor = (key) => (INITIATIVES.find(i => i.key === key) || {}).color || '#94a3b8';

const InitiativePicker = ({ value = [], onChange, testId }) => {
  const set = new Set(value || []);
  const toggle = (k) => {
    const next = new Set(set);
    if (next.has(k)) next.delete(k); else next.add(k);
    onChange(Array.from(next));
  };
  return (
    <div className="space-y-1.5" data-testid={testId || 'initiative-picker'}>
      <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        <Sparkles className="w-3.5 h-3.5" />
        A Pollen Dot of Care
      </div>
      <p className="text-[11px] text-muted-foreground -mt-0.5">
        Tag this place with the initiatives it embodies. Tagged vendors carry a coloured halo on share maps.
      </p>
      <div className="flex flex-wrap gap-1.5">
        {INITIATIVES.map(i => {
          const on = set.has(i.key);
          return (
            <button
              type="button"
              key={i.key}
              onClick={() => toggle(i.key)}
              data-testid={`initiative-toggle-${i.key}`}
              title={i.full}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                on
                  ? `${i.activeBg} ${i.activeBorder} ${i.activeText} shadow-sm`
                  : `${i.bg} ${i.border} ${i.text} hover:opacity-80`
              }`}
            >
              <span className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle" style={{ background: on ? '#fff' : i.color }} />
              {i.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default InitiativePicker;
